<template>
  <div class="au-root">
    <div class="au-header">
      <div>
        <h1 class="au-title">Uso de IA</h1>
        <p class="au-subtitle">Consumo de tokens e custo da API Anthropic</p>
      </div>
      <div class="au-period-tabs">
        <button
          v-for="p in periods"
          :key="p.key"
          class="au-period-btn"
          :class="{ 'au-period-btn--active': period === p.key }"
          @click="setPeriod(p.key)"
        >{{ p.label }}</button>
      </div>
    </div>

    <div v-if="loading" class="au-loading">Carregando...</div>
    <div v-else-if="error" class="au-error">{{ error }}</div>
    <template v-else>

      <!-- Big numbers -->
      <div class="au-cards">
        <div class="au-card">
          <div class="au-card__label">Custo total</div>
          <div class="au-card__value au-card__value--accent">${{ formatUsd(data.total_cost_usd) }}</div>
          <div class="au-card__sub">USD</div>
        </div>
        <div class="au-card">
          <div class="au-card__label">Total de chamadas</div>
          <div class="au-card__value">{{ formatNum(data.total_calls) }}</div>
          <div class="au-card__sub">requisições</div>
        </div>
        <div class="au-card">
          <div class="au-card__label">Tokens de entrada</div>
          <div class="au-card__value">{{ formatNum(data.total_input_tokens) }}</div>
          <div class="au-card__sub">${{ formatUsd(data.total_input_tokens * 0.80 / 1000000) }}</div>
        </div>
        <div class="au-card">
          <div class="au-card__label">Tokens de saída</div>
          <div class="au-card__value">{{ formatNum(data.total_output_tokens) }}</div>
          <div class="au-card__sub">${{ formatUsd(data.total_output_tokens * 4.00 / 1000000) }}</div>
        </div>
      </div>

      <!-- By module table -->
      <div class="au-section">
        <h2 class="au-section__title">Por módulo</h2>
        <div v-if="!data.by_module || !data.by_module.length" class="au-empty">
          Nenhuma chamada registrada no período.
        </div>
        <div v-else class="au-table-wrap">
          <table class="au-table">
            <thead>
              <tr>
                <th>Módulo</th>
                <th class="au-th--num">Chamadas</th>
                <th class="au-th--num">Tokens entrada</th>
                <th class="au-th--num">Tokens saída</th>
                <th class="au-th--num">Custo (USD)</th>
                <th class="au-th--bar"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in data.by_module" :key="m.module">
                <td>
                  <span class="au-module-badge" :class="moduleBadgeClass(m.module)">
                    {{ moduleLabel(m.module) }}
                  </span>
                </td>
                <td class="au-td--num">{{ formatNum(m.calls) }}</td>
                <td class="au-td--num">{{ formatNum(m.input_tokens) }}</td>
                <td class="au-td--num">{{ formatNum(m.output_tokens) }}</td>
                <td class="au-td--num au-td--cost">${{ formatUsd(m.cost_usd) }}</td>
                <td class="au-td--bar">
                  <div class="au-bar-wrap">
                    <div class="au-bar-fill" :style="{ width: barPct(m.cost_usd) + '%' }"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Daily chart (7d/30d) -->
      <div v-if="data.by_day && data.by_day.length > 1" class="au-section">
        <h2 class="au-section__title">Evolução diária</h2>
        <div class="au-chart">
          <div class="au-chart__bars">
            <div
              v-for="d in data.by_day"
              :key="d.day"
              class="au-chart__col"
              :title="`${d.day}: $${formatUsd(d.cost_usd)} · ${formatNum(d.input_tokens + d.output_tokens)} tokens`"
            >
              <div class="au-chart__bar" :style="{ height: chartBarH(d.cost_usd) + '%' }"></div>
              <div class="au-chart__label">{{ formatDay(d.day) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="au-pricing-note">
        Preços: Claude Haiku 4.5 — entrada $0.80/M tokens · saída $4.00/M tokens
      </div>

    </template>
  </div>
</template>

<script>
import { useApi } from '@/composables/useApi'

const api = useApi()

export default {
  name: 'AiUsage',
  data() {
    return {
      period: 'today',
      periods: [
        { key: 'today', label: 'Hoje' },
        { key: '7d',    label: '7 dias' },
        { key: '30d',   label: '30 dias' },
        { key: 'all',   label: 'Tudo' },
      ],
      loading: false,
      error: '',
      data: {
        total_calls: 0,
        total_input_tokens: 0,
        total_output_tokens: 0,
        total_cost_usd: 0,
        by_module: [],
        by_day: [],
      },
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async setPeriod(p) {
      this.period = p
      await this.load()
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await api.get(`ai-usage/summary?period=${this.period}`)
        this.data = data
      } catch (e) {
        this.error = 'Erro ao carregar dados de uso.'
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    formatUsd(v) {
      const n = Number(v) || 0
      if (n === 0) return '0.000000'
      if (n < 0.0001) return n.toFixed(8)
      if (n < 0.01)   return n.toFixed(6)
      return n.toFixed(4)
    },
    formatNum(v) {
      return (Number(v) || 0).toLocaleString('pt-BR')
    },
    formatDay(d) {
      if (!d) return ''
      const parts = String(d).slice(0, 10).split('-')
      return `${parts[2]}/${parts[1]}`
    },
    barPct(cost) {
      const max = Math.max(...(this.data.by_module || []).map(m => Number(m.cost_usd) || 0))
      if (!max) return 0
      return Math.round((Number(cost) / max) * 100)
    },
    chartBarH(cost) {
      const max = Math.max(...(this.data.by_day || []).map(d => Number(d.cost_usd) || 0))
      if (!max) return 2
      return Math.max(2, Math.round((Number(cost) / max) * 100))
    },
    moduleLabel(m) {
      const map = {
        'chat':          'Chat IA',
        'chat-summary':  'Chat — Resumo',
        'chat-email':    'Chat — Email',
        'blog-content':  'Blog — Conteúdo',
        'blog-seo':      'Blog — SEO',
        'ai-insights':   'Insights',
        'heatmap':       'Heatmap',
      }
      return map[m] || m
    },
    moduleBadgeClass(m) {
      if (m.startsWith('chat'))   return 'au-badge--blue'
      if (m.startsWith('blog'))   return 'au-badge--purple'
      if (m === 'heatmap')        return 'au-badge--orange'
      if (m === 'ai-insights')    return 'au-badge--green'
      return 'au-badge--gray'
    },
  },
}
</script>

<style scoped>
.au-root {
  padding: 24px;
  max-width: 1000px;
  --au-border: rgba(255,255,255,0.08);
  --au-card-bg: rgba(255,255,255,0.04);
  --au-text: #e2e8f0;
  --au-muted: rgba(255,255,255,0.4);
  --au-accent: #E86A10;
}

.au-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}
.au-title  { font-size: 20px; font-weight: 700; color: #fff; margin: 0 0 4px; }
.au-subtitle { font-size: 13px; color: var(--au-muted); margin: 0; }

.au-period-tabs { display: flex; gap: 4px; }
.au-period-btn {
  background: rgba(255,255,255,0.05);
  border: 1px solid var(--au-border);
  color: var(--au-muted);
  border-radius: 7px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
}
.au-period-btn--active { background: var(--au-accent); border-color: var(--au-accent); color: #fff; }
.au-period-btn:not(.au-period-btn--active):hover { color: #fff; background: rgba(255,255,255,0.09); }

.au-loading { color: var(--au-muted); padding: 40px 0; text-align: center; font-size: 14px; }
.au-error   { color: #f87171; padding: 16px; background: rgba(248,113,113,0.08); border-radius: 10px; font-size: 13px; }

/* Cards */
.au-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 24px; }
@media (max-width: 800px) { .au-cards { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 500px) { .au-cards { grid-template-columns: 1fr; } }

.au-card {
  background: var(--au-card-bg);
  border: 1px solid var(--au-border);
  border-radius: 12px;
  padding: 18px 20px;
}
.au-card__label { font-size: 11px; font-weight: 600; color: var(--au-muted); text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px; }
.au-card__value { font-size: 26px; font-weight: 700; color: var(--au-text); line-height: 1; }
.au-card__value--accent { color: var(--au-accent); }
.au-card__sub { font-size: 11px; color: var(--au-muted); margin-top: 5px; }

/* Section */
.au-section { margin-bottom: 24px; }
.au-section__title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: var(--au-muted); margin: 0 0 12px; }
.au-empty { color: var(--au-muted); font-size: 13px; padding: 20px 0; }

/* Table */
.au-table-wrap { overflow-x: auto; border-radius: 12px; border: 1px solid var(--au-border); }
.au-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.au-table thead tr { background: rgba(255,255,255,0.03); border-bottom: 1px solid var(--au-border); }
.au-table th { padding: 10px 14px; text-align: left; color: var(--au-muted); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; white-space: nowrap; }
.au-th--num { text-align: right; }
.au-th--bar { width: 100px; }
.au-table td { padding: 11px 14px; border-bottom: 1px solid rgba(255,255,255,0.04); color: rgba(255,255,255,0.75); vertical-align: middle; }
.au-table tbody tr:last-child td { border-bottom: none; }
.au-table tbody tr:hover td { background: rgba(255,255,255,0.02); }
.au-td--num  { text-align: right; font-variant-numeric: tabular-nums; }
.au-td--cost { color: var(--au-accent); font-weight: 600; }
.au-td--bar  { width: 100px; }

/* Module badges */
.au-module-badge { display: inline-block; padding: 3px 10px; border-radius: 99px; font-size: 11px; font-weight: 600; }
.au-badge--blue   { background: rgba(59,130,246,0.15);  color: #60a5fa; }
.au-badge--purple { background: rgba(139,92,246,0.15);  color: #c4b5fd; }
.au-badge--orange { background: rgba(232,106,16,0.15);  color: #fb923c; }
.au-badge--green  { background: rgba(16,185,129,0.15);  color: #34d399; }
.au-badge--gray   { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.6); }

/* Bar in table */
.au-bar-wrap { width: 100%; height: 5px; background: rgba(255,255,255,0.07); border-radius: 99px; overflow: hidden; }
.au-bar-fill { height: 100%; background: var(--au-accent); border-radius: 99px; transition: width .3s ease; }

/* Daily chart */
.au-chart { background: var(--au-card-bg); border: 1px solid var(--au-border); border-radius: 12px; padding: 20px; }
.au-chart__bars { display: flex; align-items: flex-end; gap: 6px; height: 120px; overflow-x: auto; padding-bottom: 24px; position: relative; }
.au-chart__col { display: flex; flex-direction: column; align-items: center; justify-content: flex-end; flex: 1; min-width: 28px; height: 100%; gap: 4px; cursor: default; }
.au-chart__bar { width: 100%; background: var(--au-accent); border-radius: 4px 4px 0 0; opacity: 0.8; transition: opacity .15s; min-height: 3px; }
.au-chart__col:hover .au-chart__bar { opacity: 1; }
.au-chart__label { font-size: 9px; color: var(--au-muted); white-space: nowrap; position: absolute; bottom: 0; }

/* Pricing note */
.au-pricing-note { font-size: 11px; color: var(--au-muted); margin-top: 8px; padding: 10px 14px; background: rgba(255,255,255,0.03); border-radius: 8px; border: 1px solid var(--au-border); }
</style>
